import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import venueService from '../../../services/venueService';
import type { VenueType, VenueStatus } from '../../../types/venue';

const VENUE_TYPES: VenueType[] = ['RESTAURANT', 'HOTEL', 'WINE_BAR', 'RETAIL', 'PRIVATE'];
const VENUE_STATUSES: VenueStatus[] = ['ACTIVE', 'INACTIVE'];

interface FormData {
  name: string;
  shortName: string;
  type: VenueType | '';
  status: VenueStatus;
  defaultCurrency: string;
  address: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  website: string;
}

const INITIAL_FORM: FormData = {
  name: '',
  shortName: '',
  type: '',
  status: 'ACTIVE',
  defaultCurrency: 'EUR',
  address: '',
  city: '',
  country: '',
  email: '',
  phone: '',
  website: '',
};

const VenueForm: React.FC = () => {
  const navigate = useNavigate();
  const { venueId } = useParams<{ venueId: string }>();
  const theme = useTheme();
  const isEdit = Boolean(venueId);

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!isEdit || !venueId) return;
    setLoading(true);
    venueService.getVenue(venueId)
      .then(venue => {
        setFormData({
          name: venue.name,
          shortName: venue.shortName ?? '',
          type: venue.type,
          status: venue.status,
          defaultCurrency: venue.defaultCurrency,
          address: venue.address ?? '',
          city: venue.city ?? '',
          country: venue.country ?? '',
          email: venue.email ?? '',
          phone: venue.phone ?? '',
          website: venue.website ?? '',
        });
      })
      .catch(() => setApiError('Failed to load venue.'))
      .finally(() => setLoading(false));
  }, [isEdit, venueId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = 'Venue name is required.';
    if (!formData.type) newErrors.type = 'Type is required.';
    if (!formData.defaultCurrency.trim()) newErrors.defaultCurrency = 'Currency is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setApiError('');
    try {
      const payload = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || undefined,
        type: formData.type as VenueType,
        status: formData.status,
        defaultCurrency: formData.defaultCurrency.trim().toUpperCase(),
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        country: formData.country.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        website: formData.website.trim() || undefined,
        active: true,
      };
      if (isEdit && venueId) {
        await venueService.updateVenue(venueId, payload);
        navigate(`/admin/venues/${venueId}`);
      } else {
        const created = await venueService.createVenue(payload as Parameters<typeof venueService.createVenue>[0]);
        navigate(`/admin/venues/${created._id}`);
      }
    } catch (err) {
      console.error('Failed to save venue', err);
      setApiError('Failed to save venue. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/venues')} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Venues
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? 'Edit Venue' : 'New Venue'}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* Name row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Venue Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={Boolean(errors.name)}
              helperText={errors.name}
              fullWidth
              required
            />
            <TextField
              label="Short Name"
              name="shortName"
              value={formData.shortName}
              onChange={handleInputChange}
              placeholder="e.g. Maison"
              sx={{ minWidth: 160 }}
            />
          </Box>

          {/* Type + Currency */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required error={Boolean(errors.type)}>
              <InputLabel>Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                label="Type"
                onChange={e => {
                  setFormData(prev => ({ ...prev, type: e.target.value as VenueType }));
                  setErrors(prev => ({ ...prev, type: undefined }));
                }}
              >
                {VENUE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
              {errors.type && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.type}</Typography>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value as VenueStatus }))}
              >
                {VENUE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            <TextField
              label="Default Currency"
              name="defaultCurrency"
              value={formData.defaultCurrency}
              onChange={handleInputChange}
              error={Boolean(errors.defaultCurrency)}
              helperText={errors.defaultCurrency}
              placeholder="EUR"
              required
              sx={{ minWidth: 140 }}
              slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
            />
          </Box>

          {/* Address */}
          <TextField
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            fullWidth
            placeholder="Street address"
          />

          {/* City + Country */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="City" name="city" value={formData.city} onChange={handleInputChange} fullWidth />
            <TextField label="Country" name="country" value={formData.country} onChange={handleInputChange} fullWidth />
          </Box>

          {/* Contact */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Contact Email" name="email" value={formData.email} onChange={handleInputChange} fullWidth type="email" />
            <TextField label="Contact Phone" name="phone" value={formData.phone} onChange={handleInputChange} fullWidth />
          </Box>

          <TextField label="Website" name="website" value={formData.website} onChange={handleInputChange} fullWidth placeholder="https://" />

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/admin/venues')} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 130 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Create Venue'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default VenueForm;
